/**
 * `assetDto.ts` — wire shape of an asset row in the
 * `projectstore-metadata` Dynamo table.
 *
 * Mirrors `AssetData` (`Models/Data/AssetData.cs`) +
 * `DynamoClient.ToAssetData` (`shared-application-code/.../DynamoClient.cs:672`).
 * Field names are PascalCase to match Unity's JSON exactly so a
 * marshalled `unmarshall(item)` lines up 1:1.
 *
 * Required-vs-optional:
 *   - `ID` and `Format` are the minimum needed to fetch the GLB.
 *   - `ObjectKey` is what the Unity client actually uses for S3 access
 *     (`AssetData.ObjectKey` returns `"{ID}.rtpackage"` when null —
 *     mirrored by {@link assetObjectKey}). For `.glb` assets the row
 *     either persists `ObjectKey = "{ID}.glb"` explicitly or relies on
 *     the same default convention via `GetStoragePathForAsset`.
 *   - Everything else (FileName, FileSize, Provider, AssetVersion,
 *     ImporterVersion, Scale, …) is read for inspector / library UI;
 *     the GLB-load path doesn't need them.
 */

export interface AssetDto {
  /** Unity `ID` — the canonical asset id used for S3 lookup. */
  readonly ID: string;
  /**
   * Mirrors `AssetData.FileName` (display-only; not the S3 key).
   */
  readonly FileName?: string;
  readonly FilePath?: string;
  readonly FileSize?: number;
  /**
   * Asset format — one of `AssetInfo.NativeFormats` (`GLB`, `GLTF`,
   * `OBJ`, `FBX`, …). Used by Unity's `IDataStorageModelExtensions.
   * GetStoragePathForAsset` to compute the on-disk filename / S3 key
   * fallback (`"{ID}.{format.toLowerCase()}"`).
   */
  readonly Format?: string;
  /**
   * Explicit S3 object suffix override. When unset Unity falls back to
   * `"{ID}.rtpackage"` (per `AssetData.ObjectKey`). Most cloud-imported
   * assets in current accounts persist this verbatim.
   */
  readonly ObjectKey?: string;
  readonly Provider?: string;
  readonly Scale?: number;
  readonly LastModified?: string;
  readonly UserTag?: string;
  readonly ImporterVersion?: string;
  readonly AssetVersion?: number;
  readonly HasLicensingRestrictions?: boolean;
  readonly TriangleCount?: number;

  /** Pass-through bucket for forward-compat. */
  readonly [extra: string]: unknown;
}

/**
 * S3 object suffix for an asset row. Mirrors Unity's two-step
 * resolution:
 *
 *   1. If the row persists `ObjectKey` (the post-import path always
 *      does — `ProjectDataModel.cs:2491`), use it verbatim.
 *   2. Otherwise compute the suffix from `Format` exactly like
 *      `IDataStorageModelExtensions.GetStoragePathForAsset`:
 *        - `GLTF`   → `{ID}.gltfzip`
 *        - `PCROOT` → `{ID}.pcrootzip`
 *        - other native formats (`GLB`, `OBJ`, `FBX`, …) →
 *          `{ID}.{format.toLowerCase()}`
 *        - `USD`    → `{ID}.usd`
 *        - else (legacy `RTSL` / unset) → `{ID}.rtpackage`
 *
 * Note: `AssetData.ObjectKey` getter alone falls back to `.rtpackage`
 * regardless of `Format`, but Unity's *S3 read path* always goes
 * through `GetStoragePathForAsset` which honors `Format`. Mirroring
 * the read path matters because cloud-imported assets sometimes have
 * the row written without `ObjectKey` populated (older importers)
 * even though the actual S3 object lives at `{ID}.glb`.
 */
export function assetObjectKey(dto: AssetDto): string {
  if (dto.ObjectKey && dto.ObjectKey.length > 0) return dto.ObjectKey;
  return assetObjectKeyForFormat(dto.ID, dto.Format ?? null);
}

// Unity's exact set (`AssetInfo.NativeFormats`, AssetInfo.cs:10). NOTE:
// "OBJ" is NOT native in Unity (converted to GLB on upload); "MP4"/"PXZ"
// are stored raw. In practice `ObjectKey` is always persisted by Unity's
// `CreateAssetData`, so this fallback rarely decides anything.
const NATIVE_FORMATS: ReadonlySet<string> = new Set([
  "GLB",
  "GLTF",
  "FBX",
  "PCROOT",
  "MP4",
  "PXZ",
]);

/**
 * Object key for a newly persisted asset format. This is the write-side
 * inverse of {@link assetObjectKey}; keeping both on one function prevents a
 * `Format: "GLTF"` row from being paired with a `.glb` object key.
 */
export function assetObjectKeyForFormat(id: string, format: string | null): string {
  if (format && NATIVE_FORMATS.has(format)) {
    if (format === "GLTF") return `${id}.gltfzip`;
    if (format === "PCROOT") return `${id}.pcrootzip`;
    return `${id}.${format.toLowerCase()}`;
  }
  if (format === "USD") return `${id}.usd`;
  return `${id}.rtpackage`;
}
